import { keepPreviousData, useInfiniteQuery, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { fetchAllPages, type Versioned } from './client';
import { useApi } from './context';
import type {
  EntryPointStatus,
  ActivityFilter,
  AttachmentListFilter,
  Deployment,
  DeploymentListFilter,
  Network,
  RequestListFilter,
  ResourceType,
  RunListFilter,
} from './types';

export const queryKeys = {
  me: ['me'] as const,
  appVersion: ['app-version'] as const,
  changelog: ['app-version', 'changelog'] as const,
  blueprints: (cursor: string | null) => ['blueprints', cursor] as const,
  blueprint: (id: string) => ['blueprint', id] as const,
  versions: (id: string, cursor: string | null) => ['blueprint', id, 'versions', cursor] as const,
  version: (id: string, version: number) => ['blueprint', id, 'version', version] as const,
  deployments: (blueprintId: string, cursor: string | null) => ['deployments', blueprintId, cursor] as const,
  allDeployments: (blueprintId: string) => ['deployments', blueprintId, 'all'] as const,
  deploymentList: (filter: DeploymentListFilter, cursor: string | null) =>
    ['deployments', 'list', filter, cursor] as const,
  runList: (filter: RunListFilter, cursor: string | null) => ['runs', 'list', filter, cursor] as const,
  activeRuns: ['runs', 'active'] as const,
  runSummaries: (deploymentId: string, cursor: string | null) =>
    ['deployment', deploymentId, 'run-summaries', cursor] as const,
  latestPlan: (deploymentId: string) => ['deployment', deploymentId, 'latest-plan'] as const,
  deployment: (id: string) => ['deployment', id] as const,
  deploymentResources: (id: string) => ['deployment', id, 'resources'] as const,
  secrets: (id: string) => ['deployment', id, 'secrets'] as const,
  variables: (id: string) => ['deployment', id, 'variables'] as const,
  activity: (id: string, filter: ActivityFilter, limit: number) =>
    ['deployment', id, 'activity', filter, limit] as const,
  resourceSettings: (id: string, resourceId: string) =>
    ['deployment', id, 'resource-settings', resourceId] as const,
  runs: (deploymentId: string, cursor: string | null) =>
    ['deployment', deploymentId, 'runs', cursor] as const,
  run: (id: string) => ['run', id] as const,
  runVariableChanges: (id: string) => ['run', id, 'variable-changes'] as const,
  resourceTypes: ['resource-types'] as const,
  providers: ['providers'] as const,
  secretStores: ['secret-stores'] as const,
  cloudAccounts: (cursor: string | null) => ['cloud-accounts', cursor] as const,
  allCloudAccounts: ['cloud-accounts', 'all'] as const,
  cloudAccount: (id: string) => ['cloud-account', id] as const,
  // Feature spec 17: every query of one network sits under `['network', id]`, so one invalidation refreshes them.
  networks: (cursor: string | null) => ['networks', cursor] as const,
  network: (id: string) => ['network', id] as const,
  addressPlan: (id: string) => ['network', id, 'address-plan'] as const,
  attachments: (id: string, filter: AttachmentListFilter, cursor: string | null) =>
    ['network', id, 'attachments', filter, cursor] as const,
  eligibleDeployments: (id: string) => ['network', id, 'eligible-deployments'] as const,
  networkDns: (id: string) => ['network', id, 'dns'] as const,
  networkDiagnostics: (id: string) => ['network', id, 'diagnostics'] as const,
  networkRuns: (id: string, cursor: string | null) => ['network', id, 'runs', cursor] as const,
  networkActiveRuns: (id: string) => ['network', id, 'active-runs'] as const,
  networkActivity: (id: string, filter: ActivityFilter) => ['network', id, 'activity', filter] as const,
  networkKinds: (networkId: string | null) => ['network-kinds', networkId] as const,
  networkRequests: (id: string, filter: RequestListFilter) => ['network', id, 'requests', filter] as const,
  entryPointStatus: (id: string, epId: string) => ['network', id, 'entry-point', epId, 'status'] as const,
};

/** Cursor paging with a stack of visited cursors for "previous". */
export function usePager() {
  const [stack, setStack] = useState<(string | null)[]>([null]);
  const cursor = stack[stack.length - 1];
  return {
    cursor,
    page: stack.length,
    hasPrevious: stack.length > 1,
    next: (nextCursor: string) => setStack((s) => [...s, nextCursor]),
    previous: () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)),
    reset: () => setStack([null]),
  };
}

export const PAGE_SIZE = 50;

// The catalog and providers are cached for the session (spec 6.3.1).
export function useResourceTypes() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.resourceTypes,
    queryFn: async () => (await api.listResourceTypes()).items,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useResourceTypeMap(): Map<string, ResourceType> {
  const { data } = useResourceTypes();
  return useMemo(() => new Map((data ?? []).map((t) => [t.type, t])), [data]);
}

export function useProviders() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.providers,
    queryFn: async () => (await api.listProviders()).items,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useSecretStores(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.secretStores,
    queryFn: async () => (await api.listSecretStores()).items,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useBlueprint(id: string, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: queryKeys.blueprint(id), queryFn: () => api.getBlueprint(id), enabled });
}

export function useBlueprintPage(cursor: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.blueprints(cursor),
    queryFn: () => api.listBlueprints({ limit: PAGE_SIZE, cursor }),
    placeholderData: keepPreviousData,
  });
}

export function useAllDeployments(blueprintId: string, options: { refetchInterval?: number } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.allDeployments(blueprintId),
    queryFn: () => fetchAllPages((page) => api.listDeployments(blueprintId, page)),
    refetchInterval: options.refetchInterval,
  });
}

export function useDeployment(
  id: string,
  options: {
    refetchInterval?: UseQueryOptions<Versioned<Deployment>>['refetchInterval'];
    enabled?: boolean;
  } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.deployment(id),
    queryFn: () => api.getDeployment(id),
    refetchInterval: options.refetchInterval,
    enabled: options.enabled ?? true,
  });
}

export function useDeploymentResources(
  id: string | null,
  options: { refetchInterval?: number | false } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.deploymentResources(id ?? ''),
    queryFn: async () => (await api.getDeploymentResources(id!)).items,
    enabled: !!id,
    refetchInterval: options.refetchInterval,
  });
}

/** Drift is checked whenever the view opens; the server caches its check for 30 seconds. */
export function useSecrets(deploymentId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.secrets(deploymentId ?? ''),
    queryFn: ({ signal }) => api.getSecrets(deploymentId!, signal),
    enabled: !!deploymentId,
    staleTime: 30_000,
  });
}

/**
 * Every app's variables of a deployment (spec 6.10.2): one query per deployment, shared by the Variables
 * window, the node footers, the inspector's card and the resources table; stale after 30 s, never polled.
 */
export function useDeploymentVariables(deploymentId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.variables(deploymentId ?? ''),
    queryFn: ({ signal }) => api.getVariables(deploymentId!, { signal }),
    enabled: !!deploymentId,
    staleTime: 30_000,
  });
}

/**
 * A deployment's activity (spec 6.11), newest first, page by page ("Load more"). Under the deployment's key, so
 * every write that invalidates the deployment's queries refreshes it.
 */
export function useActivity(deploymentId: string | null, filter: ActivityFilter, limit = 50) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: queryKeys.activity(deploymentId ?? '', filter, limit),
    queryFn: ({ pageParam, signal }) =>
      api.listActivity(deploymentId!, filter, { limit, cursor: pageParam }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!deploymentId,
    staleTime: 30_000,
  });
}

export function useResourceSettings(deploymentId: string | null, resourceId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.resourceSettings(deploymentId ?? '', resourceId),
    queryFn: ({ signal }) => api.getResourceSettings(deploymentId!, resourceId, signal),
    enabled: !!deploymentId,
    staleTime: 30_000,
  });
}

/** The first page of a deployment's runs (newest first), shared with the runs table. */
export function useRecentRuns(
  deploymentId: string | null,
  options: { refetchInterval?: number | false } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.runs(deploymentId ?? '', null),
    queryFn: () => api.listRuns(deploymentId!, { limit: PAGE_SIZE, cursor: null }),
    enabled: !!deploymentId,
    refetchInterval: options.refetchInterval,
  });
}

/** How often the active runs are polled (spec 6.1.7); never in hidden tabs. */
export const ACTIVE_RUNS_POLL_MS = 10_000;

/**
 * The installation's active runs (`GET /runs?status=active`), shared by the active-runs pill, the runs list's
 * pinned section and the lists' "active run" columns. Polled every 10 s while the tab is visible (TanStack
 * Query pauses `refetchInterval` in hidden tabs); it never opens event streams.
 */
export function useActiveRuns() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.activeRuns,
    queryFn: () => api.listAllRuns({ status: 'active' }, { limit: 50 }),
    refetchInterval: ACTIVE_RUNS_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/** A page of `GET /deployments` (all blueprints) with filters. */
export function useDeploymentList(filter: DeploymentListFilter, cursor: string | null, limit = PAGE_SIZE) {
  const api = useApi();
  return useQuery({
    queryKey: [...queryKeys.deploymentList(filter, cursor), limit],
    queryFn: () => api.listAllDeployments(filter, { limit, cursor }),
    placeholderData: keepPreviousData,
  });
}

/** A page of `GET /runs` (run summaries of all deployments) with filters. */
export function useRunList(filter: RunListFilter, cursor: string | null, enabled = true, limit = PAGE_SIZE) {
  const api = useApi();
  return useQuery({
    queryKey: [...queryKeys.runList(filter, cursor), limit],
    queryFn: () => api.listAllRuns(filter, { limit, cursor }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAllCloudAccounts(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.allCloudAccounts,
    queryFn: () => fetchAllPages((page) => api.listCloudAccounts(page)),
    enabled,
  });
}

// ---- Networks (feature spec 17, phase 1) ----

/** How often a network page polls while the network has an active or queued run (spec 6.15.4). */
export const NETWORK_POLL_MS = 3000;

/** The first page (or the page at `cursor`) of `GET /networks`. */
export function useNetworkPage(cursor: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.networks(cursor),
    queryFn: () => api.listNetworks({ limit: PAGE_SIZE, cursor }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** `GET /networks/{id}` with its ETag; polled every 3 s while the network has an active or queued run. */
export function useNetwork(id: string, options: { enabled?: boolean; poll?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.network(id),
    queryFn: () => api.getNetwork(id),
    enabled: (options.enabled ?? true) && !!id,
    refetchInterval: options.poll
      ? (query) => {
          const network = query.state.data?.data as Network | undefined;
          return network && (network.activeRun || network.queuedRuns > 0) ? NETWORK_POLL_MS : false;
        }
      : undefined,
  });
}

/** The network's queued and running runs (`GET /runs?networkId=<id>&status=active`). */
export function useNetworkActiveRuns(id: string, refetchInterval: number | false = false) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.networkActiveRuns(id),
    queryFn: () => api.listAllRuns({ status: 'active', networkId: id }, { limit: 50 }),
    enabled: !!id,
    refetchInterval,
  });
}

export function useAddressPlan(id: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.addressPlan(id),
    queryFn: () => api.getAddressPlan(id),
    enabled: enabled && !!id,
  });
}

export function useAttachments(
  id: string,
  filter: AttachmentListFilter,
  cursor: string | null,
  options: { enabled?: boolean; refetchInterval?: number | false; limit?: number } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: [...queryKeys.attachments(id, filter, cursor), options.limit ?? PAGE_SIZE],
    queryFn: () => api.listAttachments(id, filter, { limit: options.limit ?? PAGE_SIZE, cursor }),
    placeholderData: keepPreviousData,
    enabled: (options.enabled ?? true) && !!id,
    refetchInterval: options.refetchInterval,
  });
}

/** Every page of `GET /networks/{id}/eligible-deployments` (Attach deployments…). */
export function useEligibleDeployments(id: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.eligibleDeployments(id),
    queryFn: () => fetchAllPages((page) => api.listEligibleDeployments(id, page)),
    enabled: enabled && !!id,
  });
}

export function useNetworkDns(id: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.networkDns(id),
    queryFn: () => api.getNetworkDns(id),
    enabled: enabled && !!id,
  });
}

/**
 * The dry run of the network's current settings (`POST /networks/{id}/validate` with an empty patch): the resolver's
 * warnings about the saved network (feature spec 17, phase 4: `EGRESS_NOT_FILTERED`, `AWS_EGRESS_PUBLIC_SUBNETS`).
 */
export function useNetworkDiagnostics(id: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.networkDiagnostics(id),
    queryFn: async () => (await api.validateNetwork(id, {})).diagnostics,
    enabled: enabled && !!id,
    // Only network admins may run the dry run; the others see the warnings that don't need it.
    retry: false,
  });
}

export function useNetworkRuns(id: string, cursor: string | null, refetchInterval: number | false = false) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.networkRuns(id, cursor),
    queryFn: () => api.listNetworkRuns(id, { limit: PAGE_SIZE, cursor }),
    placeholderData: keepPreviousData,
    enabled: !!id,
    refetchInterval,
  });
}

/** A network's activity (spec 6.11), newest first, page by page ("Load more"). */
export function useNetworkActivity(id: string | null, filter: ActivityFilter, limit = 50) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...queryKeys.networkActivity(id ?? '', filter), limit],
    queryFn: ({ pageParam, signal }) =>
      api.listNetworkActivity(id!, filter, { limit, cursor: pageParam }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** The kinds registry (`GET /network-kinds`, with `networkId` for a network's `needs-provider` reasons). */
export function useNetworkKinds(networkId: string | null = null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.networkKinds(networkId),
    queryFn: () => api.getNetworkKinds(networkId),
    staleTime: 5 * 60_000,
    enabled,
  });
}

// ---- Entry points (feature spec 17, phase 3) ----

/** Whether an entry point's status still changes on its own (polled every 10 s, spec 11 6.15.10). */
export function entryPointStatusMoving(status: EntryPointStatus | undefined): boolean {
  return (
    !!status &&
    (status.pendingOrigins.length > 0 ||
      status.rotations.length > 0 ||
      status.domains.some((d) => d.state === 'waiting-for-dns'))
  );
}

/** `GET /networks/{id}/entry-points/{epId}/status`, polled while origins, rotations or DNS are pending. */
export function useEntryPointStatus(networkId: string, epId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.entryPointStatus(networkId, epId),
    queryFn: () => api.getEntryPointStatus(networkId, epId),
    enabled,
    refetchInterval: (query) => (entryPointStatusMoving(query.state.data) ? 10_000 : false),
  });
}

// ---- Network requests (feature spec 17, phase 2) ----

/** Every page of `GET /networks/{id}/requests` with the filters (a network has few blueprint requests). */
export function useNetworkRequests(
  id: string | null,
  filter: RequestListFilter = {},
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.networkRequests(id ?? '', filter),
    queryFn: () => fetchAllPages((page) => api.listNetworkRequests(id!, filter, page), 100),
    enabled: (options.enabled ?? true) && !!id,
    refetchInterval: options.refetchInterval,
    placeholderData: keepPreviousData,
  });
}
